import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, from, Subject } from 'rxjs';
import { catchError, tap, take } from 'rxjs/operators';
import { ConfirmDialogService } from '../confirm-dialog/services/confirm-dialog.service';
import { SharedService } from '../core/shared.service';
import { NotificationService } from '../services/notification.service';
import { StockService } from '../services/stock.service';
import { DailyStockComponent } from './daily-stock/daily-stock.component';
import { StockInventoryComponent } from './stock-inventory/stock-inventory.component';
import { RoutingService } from '../services/routing-service.service';
import { Stock } from './models/stock.model';
import { AuthService } from '../core/auth-service';
import { BuyerService } from '../services/buyer.service';
import { StockCheck } from './models/stock-check.model';
import { AssetTransferComponent } from './asset-transfer/asset-transfer.component';
import { VehicleListValue } from '../walk-in/models/vehicle-list-value.model';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { StockInventory } from './models/stock-inventory.model';
import { NonRunnerModalComponent } from './non-runner-modal/non-runner-modal.component';

@Component({
  selector: 'app-stock',
  templateUrl: './stock.component.html',
  styleUrls: ['./stock.component.scss'],
})
export class StockComponent implements OnInit, OnDestroy {

  constructor(
    private stockService: StockService,
    private notifications: NotificationService,
    private modalService: NgbModal,
    private sharedService: SharedService,
    private confirmDialogService: ConfirmDialogService,
    private authService: AuthService,
    private routingService: RoutingService,
    private buyerService: BuyerService,
    private formBuilder: UntypedFormBuilder,
  ) { }

  @ViewChild(DailyStockComponent)
  dailyStockComponent!: DailyStockComponent;
  @ViewChild(StockInventoryComponent)
  stockInventoryComponent!: StockInventoryComponent;
  isDailyStockView = true;
  enableSaveReportBtn!: boolean;
  username: string | undefined;
  stockCheckCompleted!: Date;
  stockCheckCompletedBy!: string;
  from!: string;
  stockCheckId!: number;
  siteId!: number;
  siteName!: string;
  noVehicleInStock!: boolean;
  newStockItem!: boolean;
  public isSavedToday!: boolean;
  destroy$ = new Subject();
  nonRunner: boolean | undefined;


  ngOnInit(): void {
    this.routingService.redirectToAuthTaskIfMobile();
    this.isDailyStockView = true;
    this.from = 'DailyStock';

    const site = this.buyerService.getCurrentSiteFromStorage();
    if (site) {
      this.siteName = site.name;
      this.siteId = site.siteId;
    } else {
      this.siteName = '';
      this.siteId = 0;
    }

    this.stockService
      .getLatestStockCheck$(this.siteId)
      .pipe(
        tap((data) => {

          if (this.stockService.isDailyStockCheckRequired(data.created)) {
            this.enableSaveReportBtn = true;
          } else {
            this.stockCheckId = data.stockCheckId;
            this.stockCheckCompleted = data.created;
            this.stockCheckCompletedBy = data.username;
            this.isSavedToday = true;
            this.stockInventory();
          }
        }),
        catchError((err) => {
          this.notifications.dangerToast('Failed to load initial data', err);
          return EMPTY;
        })
      )
      .subscribe();

    this.sharedService.source
      .pipe(
        tap((value) => {
          this.noVehicleInStock = value;
        }),
        catchError((err) => {
          this.notifications.dangerToast('Failed to load initial data', err);
          return EMPTY;
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
    this.stockService.multipleVehicleDescriptions = [];
  }

  saveReport() {
    if (this.from === 'DailyStock' && this.isSavedToday) {
      return;
    }

    if (this.from === 'DailyStock') {
      this.stockService
        .saveDailyStockCheck$(this.siteId, this.dailyStockComponent.stockItems)
        .subscribe((response: StockCheck) => {
          this.username = response.username;
          this.stockCheckId = response.stockCheckId;
          this.stockCheckCompleted = response.created;
          this.stockCheckCompletedBy = response.username;
          this.enableSaveReportBtn = false;
          this.stockCheck(response.created);
        });
    } else if (this.from === 'StockInventory'
      && (this.stockInventoryComponent.stockItems.some((v) => v.leftBranch === true || v.markAsNonRunner === true))
    ) {

      if (this.nonRunner && this.isMarkedAsNonRunner()) {
        const inventory = new StockInventory();
        inventory.siteId = this.siteId;
        inventory.siteName = this.siteName;
        inventory.items = this.stockInventoryComponent.stockItems.filter((v) => v.leftBranch === true || v.markAsNonRunner === true);
        this.stockService.saveStockInventory$(inventory).subscribe(() => {
          this.enableSaveReportBtn = false;
          this.stockInventoryComponent.loadData();
        });
      } else {
        this.stockService.multipleVehicleDescriptions.push(
          ...this.stockInventoryComponent.stockItems
            .filter(item => item.leftBranch)
            .map(item => this.mapToVehicleListValue(item))
        );

        const modalRef = this.modalService.open(AssetTransferComponent, {
          keyboard: false,
          backdrop: 'static',
          centered: true,
          size: 'xl',
          scrollable: false
        });

        modalRef.componentInstance.multipleVehicleDescriptions = this.stockService.multipleVehicleDescriptions;
        modalRef.componentInstance.siteId = this.siteId;
        modalRef.componentInstance.siteName = this.siteName;
        modalRef.componentInstance.stockInventoryComponent = this.stockInventoryComponent;
        modalRef.result.then(
          () => { },
          () => { }
        );
        this.enableSaveReportBtn = false;
      }
    }
    else {
      this.stockCheck(new Date());
      this.enableSaveReportBtn = false;
    }
  }

  createVehicleItemsForm(): FormGroup {
    return this.formBuilder.group({
      spareKey: [''],
      key: [''],
      v5: [''],
      serviceHistory: [''],
      satNavCard: [''],
      other: [''],
      vanEmpty: ['']
    });
  }

  mapToVehicleListValue(stockItem: Stock): VehicleListValue {
    return new VehicleListValue([], 0, '', stockItem.fuelType, false, {
      Manufacturer: stockItem.make,
      Colour: stockItem.colour,
      Model: stockItem.model,
      EngineSize: stockItem.engineSize?.toString(),
      ImageUrl: stockItem.imageUrl ?? '../../assets/images/unknownCar.png',
      FuelType: stockItem.fuelType,
      Vrm: stockItem.vrm,
      CapCodeId: stockItem.capId?.toString(),
      Form: this.createVehicleItemsForm(),
      IsVan: stockItem.isVan,
      QuoteStateId: stockItem.quoteStateId
    });
  }


  stockCheck(created: Date) {
    const isStockCheckRequired = this.stockService.isDailyStockCheckRequired(created);
    if (!isStockCheckRequired) {
      this.isSavedToday = true;
    }

    this.sharedService.source.emit(!this.isSavedToday);
  }

  dailyStock() {
    if (!this.isSavedToday) {
      return;
    }

    this.isDailyStockView = true;
    this.from = 'DailyStock';

    this.enableSaveReportBtn = false;
  }

  stockInventory() {
    if (!this.isSavedToday) {
      return;
    }

    this.isDailyStockView = false;
    this.from = 'StockInventory';

    this.enableSaveReportBtn = false;
  }

  enableReport(event: { checked: boolean; from: string; }) {
    this.enableSaveReportBtn = event.checked;
    this.from = event.from;
  }

  enableNonRunner() {
    this.nonRunner = true;
    this.sharedService.runner.emit(true);
  }

  showSaveReportModal() {
    const isDailyStock = this.from === 'DailyStock';
    if (isDailyStock) {
      if (this.dailyStockComponent.stockItems.length > 0) {
        const nonRunners = this.dailyStockComponent.stockItems.filter(x => x.markAsNonRunner);
        if (nonRunners.length > 0) {
          this.openStockCheckCompletedModal(nonRunners)
            .pipe(take(1))
            .subscribe((completedNonRunners: Stock[]) => {
              if (!completedNonRunners?.length) {
                return;
              }
              this.confirmDialogService
                .saveStockStockCheckConfirmation(
                  completedNonRunners.length
                )
                .pipe(take(1))
                .subscribe(() => {

                  this.saveReport();
                  this.modalService.dismissAll();

                });
            });
        } else {
          this.saveReport();
          this.confirmDialogService
            .saveDailyStockCheck()
            .pipe(take(1))
            .subscribe(() => {
              this.modalService.dismissAll();
            });
        }
      } else {
        this.saveReport();
        this.confirmDialogService
          .saveDailyStockCheck()
          .pipe(take(1))
          .subscribe(() => {
            this.modalService.dismissAll();
          });
      }
    } else {
      if (this.nonRunner && this.isMarkedAsNonRunner()) {
        const nonRunners = this.stockInventoryComponent.stockItems.filter(x => x.markAsNonRunner);
        if (nonRunners.length > 0) {
          this.openStockCheckCompletedModal(nonRunners)
            .pipe(take(1))
            .subscribe((completedNonRunners: Stock[]) => {
              if (!completedNonRunners?.length) {
                return;
              }

              this.confirmDialogService
                .saveStockStockCheckConfirmation(
                  completedNonRunners.length
                )
                .pipe(take(1))
                .subscribe(() => {

                  this.saveReport();
                  this.modalService.dismissAll();

                });
            });
        }
      } else {
        this.saveReport();
       // this.modalService.dismissAll();
      }
    }
  }

  isMarkedAsNonRunner() {
    return this.stockInventoryComponent.stockItems.some(x => x.markAsNonRunner);
  }

  openStockCheckCompletedModal(nonRunners: Stock[]) {

    const modalRef = this.modalService.open(NonRunnerModalComponent, {
      windowClass: 'non-runner-modal',
      backdrop: 'static',
      keyboard: false,
      centered: true
    });

    modalRef.componentInstance.nonRunners = nonRunners;

    return from(modalRef.result);
  }

  reportData(stock: Stock) {
    if (stock.username) {
      this.username = stock.username;
    } else {
      this.username = this.authService.getUser()?.name;
    }

    if (stock.quoteStateId) {
      this.enableSaveReportBtn = false;
    }
  }
}

// buyer object  app api based on the region id and buyer object region roll collection, call api every time.
